import { Buffer } from 'buffer';
import { PrivateKey, Mnemonic, Transaction, ScheduleSignTransaction } from '@hashgraph/sdk';

import { customElement, property } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ContextChangeController } from './context-change.js';
import {
  nsSettingsServiceContext,
  nsSettingsContext,
  ltnAuthServiceContext,
  ltnAuthStateContext,
  nsDirectoryServiceContext,
} from './contexts.js';

import { ButtressDbService, buttressDbServiceContext, type ButtressEntity } from '@buttress/crag';

import { LtnAuthService } from '../ltn-auth/ltn-auth-service.js';
import { LitElement } from 'lit';

import { NSDirectoryService } from '../ns-services/ns-directory-service.js';
import { NSSettingsService, SettingsObject } from '../ns-settings/ns-settings-service.js';

import { callLambda } from '../helpers/lambda.js';
import type { LedgerEstimate } from '../helpers/ledger-estimate.js';
import {
  LedgerEstimateCache,
  ledgerStateKey,
  requestLedgerEstimate,
  type LedgerEstimateRequest,
} from '../helpers/public-ledger-estimate-api.js';
import { signatoryChangeLaunch, type RuleGroup } from '../ns-workflows/jobs/@ns/public-ledger/signatory-change.js';
import type { LedgerNetwork } from '../helpers/public-ledger-network.js';
import {
  decryptWalletSecret,
  deviceKeyReplaced,
  devicePublicKeyHex,
  encryptAndStoreWalletSecret,
  forgetWallet,
  migrateUserWallet,
  resetWalletKeyCache,
  shouldForgetOnStorageEvent,
  signDeviceChallenge,
  walletEntryState,
  walletKeyCacheStale,
  walletStorageKey,
  withDeviceEnrolLock,
  type DeviceProof,
} from '../helpers/wallet-key-store.js';
import { DeviceSignOutNotice } from '../helpers/device-sign-out-notice.js';
import {
  DEVICE_CALL_TIMEOUT_MS,
  DEVICE_LOCK_WAIT_MS,
  DeviceCheckGenerations,
  type DeviceCheckAnswer,
  deviceCheckAnswer,
  type DeviceCheckOutcome,
  deviceConfirmation,
  enrolledByAnotherTab,
  followedDeviceId,
  type DeviceWatchStart,
  mayStoreDeviceId,
  refusedSignatureAction,
  type SignedOutRowAction,
  signedOutRowAction,
  storageEventRewatches,
  watchedDeviceReplaced,
} from '../helpers/device-check.js';
import { ButtressSubscriptions } from '../helpers/buttress-subscriptions.js';
import { privateKeyFromPhrase, recoveryCheckMode, samePublicKey } from '../helpers/public-ledger-key-check.js';
import {
  extractRecoveryLetters,
  phraseCheckRefusal,
  savePhraseLetters,
  verifyWithPhrase,
} from '../helpers/phrase-check-api.js';
import { lambdaErrorCode, LambdaError } from '../helpers/lambda.js';
import { partsToSign, signatureBatchBody, type SignaturePart } from '../helpers/collect-signature-state.js';
import { approveMemoFor, signatureHex, type ProposedSettings } from '../helpers/auto-payments.js';
import { sendWithDeviceProof } from '../helpers/device-proof-retry.js';
import { withTimeout } from '../helpers/async.js';
import Errors from '../helpers/errors.js';

import {
  approveScheduleAccounts,
  checkApproveOnMirror,
  checkApproveScheduleSign,
  checkCardChangeOnMirror,
  checkStopOnMirror,
  checkSigningPart,
  checkSigningPartOnMirror,
  shownMismatch,
  type ApproveExpectation,
  type ApproveScheduleAccounts,
  type OperatingAccount,
  type ShownOnCard,
  type SignCheckContext,
  type SignCheckReason,
} from '../helpers/public-ledger-sign-check.js';
import { cardChangeScheduleAccounts } from '../helpers/auto-payment-card-change.js';
import { fetchMirrorSchedule, mirrorNetwork } from '../helpers/public-ledger-mirror.js';
import {
  DEVICE_SIGNED_OUT_COPY,
  DEVICE_RECHECKED_COPY,
  DEVICE_UNCONFIRMED_COPY,
  MIRROR_UNAVAILABLE_COPY,
  SCHEDULE_CLOSED_COPY,
  SIGN_CHECK_UNAVAILABLE_COPY,
  SIGN_MISMATCH_COPY,
  SIGN_MISMATCH_UNREPORTED_COPY,
  deviceRefusal,
} from '../helpers/public-ledger-copy.js';

import {
  isRejectedResult,
  PublicLedgerMessage,
  PublicLedgerTopic,
  PublicLedgerTransaction,
} from '../models/public-ledger.js';
import type Company from '../models/company.js';
import type Person from '../models/person.js';
import { createLogger } from './logger.js';

const logger = createLogger('public-ledger-service');

// Fired on the service when this device is signed out of the open profile's public ledger account while a tab is open
// (its plDevice row's live watch, a re-check after the realtime connection came back, a signature the server refused, or
// another tab that forgot the key), after the key is forgotten. detail: { profileId }
export const DEVICE_SIGNED_OUT_EVENT = 'public-ledger-device-signed-out';

// What a device without an id answers when a signature needs one and the load check couldn't record it
const NO_DEVICE_ID_COPY =
  "This device isn't registered with your public ledger account yet. Please reload the page and try again.";

// A recovery on a device that still held the public key found the device signed out of the account: its wallet is
// forgotten, and the next recovery checks the phrase with the server, which records the device again
const RECOVERED_SIGNED_OUT_COPY =
  'This device was signed out of your public ledger account. Please enter your recovery phrase again to add it back.';

// A signature started for one profile and the open profile changed before it was sent: nothing is sent
const PROFILE_CHANGED_COPY = 'You switched profile while signing, so nothing was signed. Please try again.';

export enum AccountType {
  PERSON = 'person',
  COMPANY = 'company',
}

// The open profile's public ledger wallet on this device, as last read from localStorage. The private key is held only as
// its stored entry, ciphertext under this browser's wallet key (helpers/wallet-key-store.ts); a signature reads and
// decrypts it from localStorage, never this copy. The recovery phrase is kept in memory only, straight after the account
// is made, for the create-account card to show it once.
export interface HederaCredentials {
  accountId: string;
  privateKeyEntry: string;
  publicKey: string;
  mnemonic?: string;
  state?: CredentialsState;
  linked?: boolean;
}

// A part public-ledger-get-scheduled-transaction-serialisation serves: its ScheduleSign bytes, the schedule they sign and
// the message part they are (null on a first part stored before parts were numbered, and on anything but a message)
interface ServedPart extends SignaturePart {
  scheduleId: string | null;
  partIndex: number | null;
}

// The device id a signature's proof was last asked for, so a refusal of that signature is known to be about that row
interface ProvedDevice {
  deviceId: string | null;
}

// A device check out for a profile: the generation it started at, and whether a tab open on the profile asked for it (a
// sign-out it finds is then announced)
interface DeviceCheckFlight {
  generation: number;
  live: boolean;
  // The one fresh check a failed check's watch asked for (it saw the row signed out): its own failure asks no more
  retry: boolean;
  promise: Promise<DeviceCheckOutcome>;
}

enum CredentialsState {
  CREDENTIALS_PENDING,
  CREDENTIALS_AVAILABLE,
  CREDENTIALS_PARTIAL,
  CREDENTIALS_UNAVAILABLE,
}

@customElement('public-ledger-service')
export class PublicLedgerService extends LitElement {
  @consume({ context: buttressDbServiceContext })
  private __db?: ButtressDbService;

  @consume({ context: ltnAuthServiceContext })
  private __auth?: LtnAuthService;

  private __authChanges = new ContextChangeController(this, ltnAuthStateContext, () => this.__authStateChanged());

  @consume({ context: nsDirectoryServiceContext })
  private __dir?: NSDirectoryService;

  private __wallet?: HederaCredentials;

  // The profile the wallet above and the device check belong to: a profile change starts both again
  private __walletProfileId: string | null = null;

  // Whether this device is signed out of the profile's account, asked before the wallet is restored (#24 phase 3)
  private __deviceCheck: Promise<DeviceCheckOutcome> | null = null;

  // The device check out for each profile: another asking while it runs (the tab back online or focused, a resync, a
  // signature's proof) waits for that one rather than starting its own
  private readonly __deviceFlights = new Map<string, DeviceCheckFlight>();

  // Which device check is each profile's current one: an older check still out stops before it acts on its answer
  private readonly __checkGenerations = new DeviceCheckGenerations();

  // Whether the last device check failed (offline, a server error): it is tried again when the tab is back online or
  // focused, the realtime connection resyncs, or before the next signature
  private __deviceCheckFailed = false;

  // The profiles this tab holds unconfirmed: a signature was refused as signed out and the device check that followed
  // couldn't answer (it failed, timed out or went stale). The wallet is kept, but every signature and device proof is
  // refused up front until a device check answers: signed in clears it, signed out forgets the wallet
  private readonly __unconfirmed = new Set<string>();

  // Which profiles' sign-outs this tab still announces (DEVICE_SIGNED_OUT_EVENT), once each
  private readonly __signOutNotice = new DeviceSignOutNotice();

  // The live watch on this device's own plDevice row
  private readonly __deviceWatch = new ButtressSubscriptions(this, () => this.__db);

  // Moves on with each watch started or stopped, so only the latest watch subscribes, whichever read resolves first
  private __watchToken = 0;

  @consume({ context: nsSettingsServiceContext })
  private __settings?: NSSettingsService;

  private __settingsChanges = new ContextChangeController(this, nsSettingsContext, () => this.__settingsUpdate());

  @property({ attribute: false })
  private __app?: SettingsObject;

  private __ledgerState = ledgerStateKey(null);

  private readonly __estimates = new LedgerEstimateCache((request) =>
    requestLedgerEstimate(this.__auth?.token, request),
  );

  async connectedCallback() {
    super.connectedCallback();
    await this.updateComplete;

    if (!this.__db) throw new Error('A DB Service Is Required!');

    if (!this.__auth) throw new Error('Auth service is required!');

    if (!this.__dir) throw new Error('Directory service is required!');

    await this.__dir.initialised;

    // Kept estimates follow the company's public ledger state (balances, topic, signature rule): dropped when it changes
    this.__ledgerState = ledgerStateKey(this.__dir.company);
    this.__dir.addEventListener('company-change', () => {
      const next = ledgerStateKey(this.__dir?.company);
      if (next === this.__ledgerState) return;
      this.__ledgerState = next;
      this.dropEstimates();
    });

    if (!this.__settings) throw new Error('Unable to find settings service');

    this.__settingsUpdate();

    // Another tab signed out of the profile's account, or forgot its key, through a sign-out it was told of
    window.addEventListener('storage', this.__onStorage);
    // Realtime activity missed while the connection was down may include a sign-out
    this.__db.addEventListener('bjs-resync', this.__onResync);
    // A device check that failed is tried again once the tab is back
    window.addEventListener('online', this.__onBack);
    window.addEventListener('focus', this.__onBack);
    this.__dir.addEventListener('profile-change', () => void this.__loadWallet());

    await this.__loadWallet();
  }

  disconnectedCallback() {
    window.removeEventListener('storage', this.__onStorage);
    window.removeEventListener('online', this.__onBack);
    window.removeEventListener('focus', this.__onBack);
    this.__db?.removeEventListener('bjs-resync', this.__onResync);
    super.disconnectedCallback();
  }

  private __settingsUpdate() {
    if (!this.__settings || !this.__settings.settings.app) return;

    // App settings
    this.__app = this.__settings.settings.app as SettingsObject;
  }

  /*************************************************
   * PUBLIC
   *************************************************/

  /**
   * The network whose public mirror a check in the browser reads: the live network in a production build, whatever the
   * company record names (helpers/public-ledger-mirror.ts mirrorNetwork). Every mirror read that guards a signature goes
   * through this, so a database that names another network can't point a check at a mirror with no history.
   */
  public mirrorNetworkFor(companyNetwork: unknown): LedgerNetwork | null {
    return mirrorNetwork(this.__app?.mode as string | undefined, companyNetwork);
  }

  /**
   * addAccount: creates the caller's public ledger account through the create-account job. The key pair is made here and
   * only its public key is sent; the server decides the rest (whether this is the company's first director, who pays, the
   * network). The key pair is kept on this device, encrypted, before the call, so a first director's set-up that stopped part
   * way (PUBLIC_LEDGER_SETUP_INCOMPLETE) is tried again with the same key and the same recovery phrase.
   * @param {string} jobId the caller's @ns/create-public-ledger-account job
   * @param {LedgerNetwork | null} network the company's network, chosen by the first director outside production; null
   * leaves it to the server (the company's stored network, or MAINNET in production)
   * @returns Promise<accountId: string, publicKey: string>
   */
  public async addAccount(
    jobId: string,
    network: LedgerNetwork | null = null,
  ): Promise<{ accountId: string; publicKey: string } | null> {
    if (!this.__db) throw new Error('Missing db service');
    if (!this.__dir) throw new Error('Missing directoryService');
    if (!this.__dir.me || !this.__dir.me.email) throw new Error('Missing user email');
    if (!this.__dir.company || !this.__dir.company.id) throw new Error('Missing directoryService company');
    const prefix = this.__dir.me.id;
    if (!prefix) throw new Error('Unable to establish Person identity');
    await this.__deviceReady();

    const { privateKey, phrase, privateKeyEntry } = await this.__pendingOrNewKeyPair(prefix);
    const publicKey = privateKey.publicKey.toStringDer();

    const output = await this.__createAccount(prefix, jobId, publicKey, network);
    if (!output) throw new Error('Failed to create an account');
    // A retry of a set-up that had already made the person's account may not hand its id back; it is on the profile
    const accountId = output.accountId || this.__dir.me.ledgerCred?.accountId;
    if (!accountId) throw new Error('Unable to generate a public ledger account');

    // The device id may be null: the accounts exist even when the server couldn't record the device, so the key is still
    // put in the wallet (the next recovery records a device)
    this.__addCredentialsToWallet(prefix, accountId, publicKey, privateKeyEntry, output.deviceId ?? null);
    if (this.__wallet) this.__wallet.mnemonic = phrase;
    this.__clearPendingKeyPair(prefix);
    await this.__watchDevice(prefix, localStorage.getItem(walletStorageKey(prefix, 'deviceId')));

    return { accountId, publicKey };
  }

  /**
   * The public key of a key pair this device generated for an account creation that hasn't finished, or null. The
   * create-account card lets a person whose profile already has this key (their account was made before the set-up stopped)
   * try again.
   */
  public pendingPublicKey(): string | null {
    const prefix = this.__dir?.me?.id;
    if (!prefix) return null;
    return localStorage.getItem(walletStorageKey(prefix, 'pendingPublicKey'));
  }

  /** The public key of the account whose key this device holds, or null. */
  public publicKeyOnDevice(): string | null {
    const prefix = this.__dir?.me?.id;
    if (!prefix) return null;
    return localStorage.getItem(walletStorageKey(prefix, 'publicKey'));
  }

  /** Whether this device holds the person's public key, so the recovery phrase check is the free local one. */
  public hasPublicKeyOnDevice(): boolean {
    const prefix = this.__dir?.me?.id;
    if (!prefix) return false;
    return recoveryCheckMode(localStorage.getItem(walletStorageKey(prefix, 'publicKey'))) === 'LOCAL';
  }

  /**
   * Start a change of who signs for the company (D8): public-ledger-request-signatory-change refuses anyone who isn't a
   * current signatory, a second open change and a change while signatures are pending (Q7). It marks the change pending
   * (awaitingUpdate) and creates the change workflow, whose first card, for the requester, holds the rule editor. Every
   * button that starts a change goes through here, so a future launcher lock has one place to hook into.
   * @returns the requester's card (job) id, when the lambda says which it is
   */
  public async requestSignatoryChange(companyId: string): Promise<{ workflowId: string | null; jobId: string | null }> {
    if (!this.__auth) throw new Error('Missing auth service');
    const output = await callLambda(
      'public-ledger/request-signatory-change',
      'POST',
      this.__auth.token,
      JSON.stringify({ companyId }),
    );
    return signatoryChangeLaunch(output.res);
  }

  /**
   * Propose the new signature rule from the change workflow's card: public-ledger-update-threshold-key checks it (every
   * group 1..n, the ledger's limits, everyone an employee, a real change, no pending signatures), starts a create-account
   * workflow for each new signatory without a public ledger account, and records the proposed key. The rule only takes
   * effect once the old and the new rule have both signed and the public ledger has applied it.
   */
  public async proposeSignatureRule(workflowId: string, accountId: string, rule: RuleGroup) {
    if (!this.__auth) throw new Error('Missing auth service');
    const body = { parentId: workflowId, accountId, rule };
    return await callLambda('public-ledger/update-threshold-key', 'POST', this.__auth.token, JSON.stringify(body));
  }

  /**
   * Start a change of who signs the company's topic messages (#38): public-ledger-request-signatory-change's twin for the
   * company topic. It refuses anyone who isn't a current signatory of the topic, a company with no topic, a second open
   * change and a change while company topic messages are still being signed. It locks the topic
   * (submitKey.awaitingUpdate) and creates the change workflow, whose first card, for the requester, holds the rule editor.
   * @returns the requester's card (job) id, when the lambda says which it is
   */
  public async requestTopicSignatoryChange(
    companyId: string,
  ): Promise<{ workflowId: string | null; jobId: string | null }> {
    if (!this.__auth) throw new Error('Missing auth service');
    const output = await callLambda(
      'public-ledger/request-topic-signatory-change',
      'POST',
      this.__auth.token,
      JSON.stringify({ companyId }),
    );
    return signatoryChangeLaunch(output.res);
  }

  /**
   * Propose the topic's new signature rule from its change workflow's card: public-ledger-update-topic-submit-key checks
   * it as update-threshold-key does, starts a create-account workflow for each new signatory without a public ledger
   * account, and records the proposed key. The rule only takes effect once the current and the new topic signatories have
   * approved it and it has been applied to the topic.
   */
  public async proposeTopicSignatureRule(workflowId: string, topicId: string, rule: RuleGroup) {
    if (!this.__auth) throw new Error('Missing auth service');
    const body = { parentId: workflowId, topicId, rule };
    return await callLambda('public-ledger/update-topic-submit-key', 'POST', this.__auth.token, JSON.stringify(body));
  }

  /**
   * Approves or rejects a change to who signs the company's topic from an approval card. Nothing is signed on the public
   * ledger: the approval is kept in nodeStream, and the change is applied once both rules have enough approvals. One
   * rejection ends the change for everyone; its reason is kept in nodeStream. A refusal throws with the lambda's message.
   */
  public async answerTopicSignatoryChange(
    jobId: string,
    transactionId: string,
    answer: 'APPROVE' | 'REJECT',
    reason?: string,
  ) {
    if (!this.__auth) throw new Error('Missing auth service');
    const trimmed = reason?.trim() || undefined;
    if (answer === 'REJECT' && !trimmed) throw new Error('Please give a reason for rejecting');

    const output = await callLambda(
      'public-ledger/answer-topic-signatory-change',
      'POST',
      this.__auth.token,
      JSON.stringify({ jobId, transactionId, answer, reason: trimmed }),
    );
    return output.res;
  }

  /**
   * recordPhraseChecked: after the Recover public ledger keys card's check of the whole phrase, has the server record it as
   * checked (ledgerCred.verifiedAt). On account creation's card, the first time, that saves the letters later checks ask for
   * (public-ledger/phrase-save); any other card only answers a check with 4 letters, so a recovery never sends a fresh share
   * of the phrase (issue #15).
   * @param {string} jobId the caller's open Recover public ledger keys card
   * @param {string} phrase
   * @param {boolean} saveLetters whether the card is account creation's (savesPhraseLetters)
   */
  public async recordPhraseChecked(jobId: string, phrase: string, saveLetters: boolean) {
    if (!this.__dir || !this.__dir.me || !this.__dir.me.id) throw new Error('Missing profile from dir service');
    // verifiedAt is first set when the letters are saved, so a profile that has it (a creation card retried after its save
    // went through) answers a check rather than sending another share of the phrase
    if (saveLetters && !this.__dir.me.ledgerCred?.verifiedAt) {
      try {
        await savePhraseLetters(this.__auth?.token, jobId, extractRecoveryLetters(phrase));
        return;
      } catch (err) {
        // Saved once only: a profile not yet showing its save answers a check instead
        const code = lambdaErrorCode(err);
        if (code !== 'PHRASE_LETTERS_SAVED' && code !== 'NOT_ACCOUNT_CREATION_CARD') throw err;
      }
    }
    try {
      const checked = await verifyWithPhrase(this.__auth?.token, this.__dir.me.id, phrase);
      if (!checked.verified) throw new Error("Your recovery phrase couldn't be recorded as checked. Please try again.");
    } catch (err) {
      // A profile whose letters were never saved has nothing to check the phrase against yet
      if (lambdaErrorCode(err) === 'NO_RECOVERY_LETTERS') return;
      throw new Error(
        phraseCheckRefusal(err, "Your recovery phrase couldn't be recorded as checked. Please try again."),
      );
    }
  }

  /**
   * recoverAccount: checks a recovery phrase against the person's stored public key and puts the key on this device.
   * When the device already holds the public key (straight after the account is made, or a device that lost only its private
   * key) the check is the free local comparison. Otherwise the server checks the key against the mirror, falling back to a
   * paid lookup by the person's account id, paid by nodeStream's platform account (at most 25 per person a day).
   * @param {string} mnemonicStr
   * @returns Promise
   */
  public async recoverAccount(mnemonicStr: string) {
    if (!this.__dir || !this.__dir.me) throw new Error('Missing profile from dir service');
    const prefix = this.__dir.me.id;
    if (!prefix) throw new Error('Unable to establish Person identity');
    await this.__deviceReady();

    let privateKey: PrivateKey;
    try {
      privateKey = await privateKeyFromPhrase(mnemonicStr);
    } catch {
      throw new Error("That isn't a valid recovery phrase. Check the words and their order.");
    }
    const publicKey = privateKey.publicKey.toStringDer();
    if (!samePublicKey(publicKey, this.__dir.me.ledgerCred?.publicKey)) {
      throw new Error("This recovery phrase doesn't match your public ledger account");
    }

    const devicePublicKey = localStorage.getItem(walletStorageKey(prefix, 'publicKey'));
    const local = recoveryCheckMode(devicePublicKey) === 'LOCAL';
    let accountId: string;
    let deviceId: string | null;
    if (local) {
      if (!samePublicKey(devicePublicKey, publicKey)) {
        throw new Error("This recovery phrase doesn't match the public ledger account on this device");
      }
      accountId = localStorage.getItem(walletStorageKey(prefix, 'accountId')) || this.__dir.me.ledgerCred?.accountId;
      deviceId = localStorage.getItem(walletStorageKey(prefix, 'deviceId'));
      if (!accountId) throw new Error('Unable to find your public ledger account id');
    } else {
      const output = await this.__getAccountId(prefix, publicKey);
      if (!output || !output.accountId) throw new Error('Public ledger account check failed to return account ID');
      accountId = output.accountId;
      deviceId = output.deviceId;
    }

    // Stored again even when the device had it: a device whose wallet key was cleared can no longer decrypt the old entry
    const privateKeyEntry = await encryptAndStoreWalletSecret(
      walletStorageKey(prefix, 'privateKey'),
      privateKey.toStringDer(),
    );
    this.__wallet = undefined;
    this.__addCredentialsToWallet(prefix, accountId, publicKey, privateKeyEntry, deviceId);
    if (local) {
      // The server wasn't asked: the device check records a device with no id, and enrols a new row when this browser's
      // device key pair was lost (the stored row's key can't be proven any more), so signing works again
      await this.__recheckDevice(prefix, false);
      if (!localStorage.getItem(walletStorageKey(prefix, 'privateKey'))) throw new Error(RECOVERED_SIGNED_OUT_COPY);
    } else {
      await this.__watchDevice(prefix, deviceId);
    }

    return this.__wallet;
  }

  /**
   * @method getCreds: waits for the device check (a signed-out device has no wallet once it answers)
   * @returns HederaCredentials | null, the wallet on this device; its private key is an encrypted entry
   */
  public async getCreds(): Promise<HederaCredentials | null> {
    await this.__deviceReady();
    this.__restoreWalletFromLocalStorage();

    if (!this.__wallet) {
      logger.warn('Could not find wallet');
      return null;
    }
    if (!this.__wallet.publicKey) {
      logger.warn('Could not find public key in the wallet');
      return null;
    }
    if (!this.__wallet.privateKeyEntry) {
      logger.warn('Could not find private key in the wallet');
      return null;
    }
    if (!this.__wallet.accountId) {
      logger.warn('Could not find account id in the wallet');
      return null;
    }

    return this.__wallet;
  }

  /**
   * Signs every transaction this person still signs on a card (the parts of a long message, or the one transaction) and
   * sends them in one call, so the server checks them all before any reaches the public ledger.
   * @param {string} workflowId
   * @param {string} jobId
   * @param {Object} lead the card's own transaction
   * @param {string[]} toSignIds the card's transactions this person hasn't signed yet
   * @param {ShownOnCard} shown what the card showed: every part must say exactly this
   */
  public async signTransactions(
    workflowId: string,
    jobId: string,
    lead: PublicLedgerTransaction,
    toSignIds: string[],
    shown: ShownOnCard,
  ): Promise<string> {
    if (!this.__dir || !this.__dir.me || !this.__dir.me.id) throw new Error('Missing dir service');
    const profileId = this.__dir.me.id;
    await this.__deviceReady();
    await this.__confirmDevice(profileId);
    // Read from storage, not memory: a device signed out in another tab has no key here either
    this.__storedPrivateKeyEntry(profileId);
    if (!lead.scheduleId) throw new Error('Missing transaction scheduleId');

    const res = await this.__getScheduledTransactionSerialisation(lead);
    // Every part still collecting signatures is served; a server without parts serves the card's own transaction only
    const served: ServedPart[] = res.parts?.length
      ? res.parts.map((part) => ({
          transactionId: part.transactionId,
          scheduleId: part.scheduleId ?? null,
          partIndex: part.partIndex ?? null,
          bytes: part.bytes,
        }))
      : [{ transactionId: lead.id, scheduleId: lead.scheduleId, partIndex: lead.partIndex ?? null, bytes: res.bytes }];
    const toSign = partsToSign(served, toSignIds);
    if (toSign.length === 0) return 'USER_ALREADY_SIGNED';

    // Every part is checked against what the card shows before the key is decrypted: one that doesn't match signs nothing
    await this.__checkBeforeSigning(toSign, lead.companyId, workflowId, jobId, shown);

    // Decrypted only here, to sign, and not kept
    const privateKey = await this.__walletPrivateKey(profileId);
    const signed: SignaturePart[] = [];
    for (const part of toSign) {
      const scheduledTransaction: Transaction = ScheduleSignTransaction.fromBytes(Buffer.from(part.bytes, 'base64'));
      const signedScheduledTx = await scheduledTransaction.sign(privateKey);
      signed.push({
        transactionId: part.transactionId,
        bytes: Buffer.from(signedScheduledTx.toBytes()).toString('base64'),
      });
    }
    // Sent with this device's proof, made again once if the server refuses it as invalid; the parts aren't signed again
    const proved: ProvedDevice = { deviceId: null };
    try {
      return await sendWithDeviceProof(
        () => this.__deviceProof(profileId, proved),
        (device) => this.__submitScheduleTransactionSignatures(signed, profileId, workflowId, jobId, device),
      );
    } catch (err) {
      throw (await this.__deviceRefused(profileId, err, proved.deviceId)) ?? err;
    }
  }

  /**
   * Signs the automatic payments approval (#25 DECISION 4.7; a set-up or a re-sign): the server built a ScheduleSign that
   * nodeStream pays for and served its body bytes. Before the key is read, the body is decoded and checked
   * (helpers/public-ledger-sign-check.ts checkApproveScheduleSign: exactly a ScheduleSign of the row's schedule, paid by the
   * account the row names and never by the signer's, the company's or the operating account), then the schedule is read
   * from the public mirror (checkApproveOnMirror: one maximum hbar allowance from the company account to the operating
   * account #24's plOperatingAccount read names, created and paid for by that payer, carrying the memo recomputed from the
   * settings the card showed). The operating account must also be the one the company's own public ledger history shows,
   * when it has any (`historyOperatingAccountId`). A mismatch is reported to the PUBLIC_LEDGER team and nothing is signed.
   *
   * The key is read from storage and decrypted only to sign (__walletPrivateKey), and only the signature (lower-case hex)
   * goes to `submit`, with this device's proof (#24 phase 3): a proof refused as invalid is made again once and `submit`
   * called with the same signature; a device refusal throws the error to show (DEVICE_SIGNED_OUT checks the device again: the key is forgotten only when that
   * check answers signed out, and held unconfirmed when it can't answer).
   */
  public async signApproval<T>(
    request: {
      workflowId: string;
      jobId: string;
      transaction: PublicLedgerTransaction;
      // base64, as public-ledger/autopay/prepare-signature answers them
      bodyBytes: string;
      // The settings the card showed
      shown: ProposedSettings;
      historyOperatingAccountId: string | null;
    },
    submit: (signed: { signature: string; device: DeviceProof }) => Promise<T>,
  ): Promise<T> {
    return this.__signServerSchedule(
      request,
      async (transaction) => ({
        memo: await approveMemoFor(transaction.companyId, request.shown),
        schedule: approveScheduleAccounts(transaction.allowance),
        onMirror: checkApproveOnMirror,
      }),
      submit,
    );
  }

  /**
   * #25 phase 6 (round 15): signs a change of the automatic payment card, its record (CARD_CHANGE) checked as signApproval
   * checks an approval: the served body is a ScheduleSign of the row's schedule paid by the platform account the row names
   * (checkApproveScheduleSign), and the schedule on the public mirror is made and paid for by the company's operating
   * account, carries the memo the card showed, and runs exactly 1 tinybar from the company account to that operating account
   * (checkCardChangeOnMirror). The rest (the history anchor, a mismatch reported, the key, the device proof) is the same.
   */
  public async signCardChange<T>(
    request: {
      workflowId: string;
      jobId: string;
      transaction: PublicLedgerTransaction;
      // base64, as public-ledger/autopay/prepare-signature answers them
      bodyBytes: string;
      // The record's memo the card showed
      memo: string;
      historyOperatingAccountId: string | null;
    },
    submit: (signed: { signature: string; device: DeviceProof }) => Promise<T>,
  ): Promise<T> {
    return this.__signServerSchedule(
      request,
      async (transaction) => ({
        memo: request.memo,
        schedule: cardChangeScheduleAccounts(transaction.cardChange),
        onMirror: checkCardChangeOnMirror,
      }),
      submit,
    );
  }

  /**
   * #40: signs a stop of automatic payments, checked as signApproval checks an approval: the served body is a ScheduleSign of
   * the row's schedule paid by the platform account the row names, and the schedule on the public mirror is made and paid
   * for by that platform account, carries the memo the card showed (who stopped them), and runs exactly one hbar allowance
   * of 0 from the company account to its operating account (checkStopOnMirror). The rest is the same.
   */
  public async signStop<T>(
    request: {
      workflowId: string;
      jobId: string;
      transaction: PublicLedgerTransaction;
      // base64, as public-ledger/autopay/prepare-signature answers them
      bodyBytes: string;
      // The stop's memo the card showed
      memo: string;
      historyOperatingAccountId: string | null;
    },
    submit: (signed: { signature: string; device: DeviceProof }) => Promise<T>,
  ): Promise<T> {
    return this.__signServerSchedule(
      request,
      async (transaction) => ({
        memo: request.memo,
        // A stop is paid by the platform account throughout, as a set-up is
        schedule: transaction.allowance?.payer === 'PLATFORM' ? approveScheduleAccounts(transaction.allowance) : null,
        onMirror: checkStopOnMirror,
      }),
      submit,
    );
  }

  // What signApproval, signCardChange and signStop share: the device, the checks of what is served and of the schedule on the mirror,
  // the key, and the signature sent with this device's proof
  private async __signServerSchedule<T>(
    request: {
      workflowId: string;
      jobId: string;
      transaction: PublicLedgerTransaction;
      bodyBytes: string;
      historyOperatingAccountId: string | null;
    },
    expect: (transaction: PublicLedgerTransaction) => Promise<{
      memo: string | null;
      schedule: ApproveScheduleAccounts | null;
      onMirror: typeof checkApproveOnMirror;
    }>,
    submit: (signed: { signature: string; device: DeviceProof }) => Promise<T>,
  ): Promise<T> {
    const profileId = this.__dir?.me?.id;
    if (!profileId) throw new Error('Missing dir profile');
    // A device signed out of the account has no key once the device check answers (#24 phase 3)
    await this.__deviceReady();
    await this.__confirmDevice(profileId);
    // Read from storage, not memory: a device signed out in another tab has no key here either
    this.__storedPrivateKeyEntry(profileId);
    const { workflowId, jobId, transaction } = request;
    if (!transaction.scheduleId) throw new Error('Missing transaction scheduleId');

    const reads = await this.__approveCheckReads(transaction.companyId);
    const { memo, schedule, onMirror: checkOnMirror } = await expect(transaction);
    if (!memo || !schedule || !reads.companyAccountId) throw new Error(SIGN_CHECK_UNAVAILABLE_COPY);
    // A change of amounts' schedule is the operating account's (#25 phase 6, G2 a): the one #24's read names, nothing else
    if (schedule.operating && schedule.creatorAccountId !== reads.operating.accountId) {
      await this.__refuseSigning(transaction.id, workflowId, jobId, 'ACCOUNT_MISMATCH');
    }
    const expected: ApproveExpectation = {
      scheduleId: transaction.scheduleId,
      companyAccountId: reads.companyAccountId,
      operatingAccountId: reads.operating.accountId,
      signPayerAccountId: schedule.signPayerAccountId,
      scheduleCreatorAccountId: schedule.creatorAccountId,
      schedulePayerAccountId: schedule.payerAccountId,
      notPayers: [
        this.__wallet?.accountId,
        this.__dir?.me?.ledgerCred?.accountId,
        reads.companyAccountId,
        reads.operating.accountId,
      ],
      memo,
    };
    // The operating account nodeStream names must be the one the public ledger's own history shows
    if (request.historyOperatingAccountId && request.historyOperatingAccountId !== reads.operating.accountId) {
      await this.__refuseSigning(transaction.id, workflowId, jobId, 'ACCOUNT_MISMATCH');
    }
    const body = Uint8Array.from(Buffer.from(request.bodyBytes, 'base64'));
    const served = checkApproveScheduleSign(body, expected);
    if (!served.ok) await this.__refuseSigning(transaction.id, workflowId, jobId, served.reason);

    // Read from the network the app is built for, never one the database alone names (helpers/public-ledger-mirror.ts)
    const network = mirrorNetwork(this.__app?.mode as string | undefined, reads.network);
    if (!network) throw new Error(SIGN_CHECK_UNAVAILABLE_COPY);
    const record = await fetchMirrorSchedule(network, transaction.scheduleId, {
      fetch: (url, init) => fetch(url, init),
      sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    });
    const onMirror = checkOnMirror(record, expected, Math.floor(Date.now() / 1000));
    if (!onMirror.ok) {
      if (onMirror.reason === 'MIRROR_UNAVAILABLE') throw new Error(MIRROR_UNAVAILABLE_COPY);
      if (onMirror.reason === 'SCHEDULE_CLOSED') throw new Error(SCHEDULE_CLOSED_COPY);
      await this.__refuseSigning(transaction.id, workflowId, jobId, onMirror.reason);
    }

    // Decrypted only here, to sign, and not kept
    const privateKey = await this.__walletPrivateKey(profileId);
    const signature = signatureHex(privateKey.sign(body));
    const proved: ProvedDevice = { deviceId: null };
    try {
      return await sendWithDeviceProof(
        () => this.__deviceProof(profileId, proved),
        (device) => submit({ signature, device }),
      );
    } catch (err) {
      throw (await this.__deviceRefused(profileId, err, proved.deviceId)) ?? err;
    }
  }

  /**
   * Rejects a scheduled transaction this person was asked to sign. One rejection ends the signing for everyone: the lambda
   * deletes the schedule on the public ledger and marks the other signatories' cards as processed. The reason is kept in
   * nodeStream and shown in the app; it never goes on the public ledger. A refusal throws with the lambda's message.
   */
  public async rejectTransaction(workflowId: string, jobId: string, transactionId: string, reason: string) {
    if (!this.__auth) throw new Error('Missing auth service');
    const trimmed = reason.trim();
    if (!trimmed) throw new Error('Please give a reason for rejecting');

    const output = await callLambda(
      'public-ledger/reject-scheduled-transaction',
      'POST',
      this.__auth.token,
      JSON.stringify({ transactionId, workflowId, jobId, reason: trimmed }),
    );
    if (!isRejectedResult(output.res)) throw new Error('Unable to reject the transaction');
  }

  /**
   * extractSigantoryIds
   * @param signatureRequirementKey
   * @param company
   * @param arr
   * @returns
   */
  extractSigantoryIds(signatureRequirementKey: any, arr: string[] = []) {
    if (signatureRequirementKey.keyType === 'ED25519') return arr;

    if (signatureRequirementKey.keyType === 'PERSON_ID' && signatureRequirementKey.signatoryId) {
      arr.push(signatureRequirementKey.signatoryId);
    } else if (signatureRequirementKey.keyType === 'KEY_LIST' || signatureRequirementKey.keyType === 'THRESHOLD_KEY') {
      signatureRequirementKey.keys.forEach((key: any) => {
        this.extractSigantoryIds(key, arr);
      });
    } else {
      throw new Error(`Unsupported keyType: ${signatureRequirementKey.keyType}`);
    }

    return arr;
  }

  /**
   * The public ledger cost of a workflow, in nodeStream credits (stage 9): from its entry (the whole workflow) or, for a
   * running one, from `fromNode` (what remains). Kept for five minutes.
   */
  public estimateWorkflow(request: { workflowId: string; fromNode?: string }): Promise<LedgerEstimate | null> {
    return this.__estimates.get(request);
  }

  /** The public ledger cost of one job card's outcomes, for the person it is assigned to. Kept for five minutes. */
  public estimateJob(jobId: string): Promise<LedgerEstimate | null> {
    return this.__estimates.get({ jobId });
  }

  /** Forgets kept estimates, e.g. when the company's public ledger state or a job's status changes. */
  public dropEstimates(matches?: (request: LedgerEstimateRequest) => boolean) {
    this.__estimates.drop(matches);
  }

  /*************************************************
   * PRIVATE
   *************************************************/

  /**
   * @method __authStateChanged
   * @returns
   */
  private async __authStateChanged() {
    if (!this.__auth?.token || !this.__dir) return;

    await this.__dir.initialised;

    await this.__loadWallet();
  }

  /**
   * The open profile's wallet, once the device check has answered: a user-keyed wallet from an older version moves to its
   * profile first, a signed-out device forgets its key, and a plain key an older version stored is encrypted.
   */
  private async __loadWallet() {
    await this.__deviceReady();
    if (!this.__dir?.me?.id) return;
    this.__restoreWalletFromLocalStorage();
    await this.__migratePlainWalletEntry();
  }

  /**
   * The device check for the open profile, started once per profile (or again after a resync). A profile change forgets
   * the wallet held in memory and the watch on the old profile's device. A check that fails (offline) keeps the wallet:
   * the server's gate still refuses a signature from a signed-out device.
   */
  private __deviceReady(): Promise<unknown> {
    const profileId = this.__dir?.me?.id ?? null;
    if (profileId !== this.__walletProfileId) {
      this.__walletProfileId = profileId;
      this.__wallet = undefined;
      this.__deviceCheck = null;
      this.__deviceCheckFailed = false;
      this.__stopWatch();
    }
    if (!profileId) return Promise.resolve();
    return this.__deviceCheck ?? this.__startDeviceCheck(profileId, false);
  }

  /**
   * Runs the device check as the current one, once at a time per profile: a check already out and still current is
   * waited for rather than another started (and announces a sign-out it finds when any caller is live); one gone stale (the
   * wallet was forgotten or replaced meanwhile) is let finish, and a new check runs after it. One that fails (offline, a
   * server error) keeps the wallet, still watches the device's row from its stored id, so a sign-out reaches this tab once
   * it is back, and is tried again later (__onBack, __onResync, before the next signature). A signed-out row that watch
   * reads isn't taken as the server's word: it asks one fresh check (`retry`), whose own failure asks no more.
   */
  private __startDeviceCheck(profileId: string, live: boolean, retry = false): Promise<DeviceCheckOutcome> {
    const running = this.__deviceFlights.get(profileId);
    let check: Promise<DeviceCheckOutcome>;
    if (running && this.__checkGenerations.isCurrent(profileId, running.generation)) {
      running.live ||= live;
      check = running.promise;
    } else if (running) {
      check = running.promise.then(() => this.__startDeviceCheck(profileId, live, retry));
    } else {
      const flight: DeviceCheckFlight = {
        generation: this.__checkGenerations.next(profileId),
        live,
        retry,
        promise: Promise.resolve<DeviceCheckOutcome>({ kind: 'stale' }),
      };
      flight.promise = this.__checkDevice(profileId, flight)
        .then(
          (outcome) => {
            if (this.__walletProfileId === profileId) this.__deviceCheckFailed = false;
            if (deviceConfirmation(outcome) !== 'unconfirmed') this.__unconfirmed.delete(profileId);
            return outcome;
          },
          async (err): Promise<DeviceCheckOutcome> => {
            logger.warn('Unable to check whether this device is signed out of the public ledger account', err);
            const failed: DeviceCheckOutcome = { kind: 'failed' };
            if (this.__walletProfileId !== profileId) return failed;
            this.__deviceCheckFailed = true;
            if (!this.__checkIsCurrent(profileId, flight.generation)) return failed;
            try {
              await this.__watchDevice(profileId, localStorage.getItem(walletStorageKey(profileId, 'deviceId')), {
                current: () => this.__checkIsCurrent(profileId, flight.generation),
                start: flight.retry ? 'retried' : 'failed',
              });
            } catch (watchErr) {
              logger.warn('Unable to watch this public ledger device', watchErr);
            }
            return failed;
          },
        )
        .finally(() => {
          if (this.__deviceFlights.get(profileId) === flight) this.__deviceFlights.delete(profileId);
        });
      this.__deviceFlights.set(profileId, flight);
      check = flight.promise;
    }
    if (this.__walletProfileId === profileId) this.__deviceCheck = check;
    return check;
  }

  // Whether a device check started at `generation` may still act: the profile is open, the check is its current one,
  // and the profile's key is still on this device
  private __checkIsCurrent(profileId: string, generation: number): boolean {
    return (
      this.__walletProfileId === profileId &&
      this.__checkGenerations.isCurrent(profileId, generation) &&
      !!localStorage.getItem(walletStorageKey(profileId, 'privateKey'))
    );
  }

  /**
   * Asks public-ledger/update-device-login-timestamp whether this device is signed out of the profile's account, before
   * the wallet is restored. Sends the device's key pair's public key (registered once, on a row that has none) and, for
   * a device holding a key but no id, records the id the server answers with; for a row replaced since (a re-enrolment),
   * the server follows the replaced row's replacedBy link to the signed-in row that took its place, and that id is stored. A row
   * whose enrolled device key isn't this browser's (the key pair was lost and a new one made) can never be proven again: a
   * new row is enrolled with the new key in its place. Then watches the device's row. A new id is stored while the id
   * asked about is still the stored one and the key is still here; anything else, and the watch, only while this is still
   * the profile's current check. The lock is waited for, and each server call under it runs, for a bounded time
   * (DEVICE_LOCK_WAIT_MS, DEVICE_CALL_TIMEOUT_MS): running out fails the check, so a tab stalled holding the lock never
   * leaves the others waiting. Returns how the check ended.
   */
  private async __checkDevice(profileId: string, flight: DeviceCheckFlight): Promise<DeviceCheckOutcome> {
    const stale: DeviceCheckOutcome = { kind: 'stale' };
    if (!this.__auth || !this.__dir || !this.__dir.me) return stale;
    const me = this.__dir.me;
    if (me.userId) {
      const profiles = this.__dir.people.map((person) => ({ id: person.id, publicKey: person.ledgerCred?.publicKey }));
      migrateUserWallet(me.userId, profiles, samePublicKey);
    }
    const current = () => this.__checkIsCurrent(profileId, flight.generation);
    // Nothing to check on a device that holds no key for the profile
    if (!current()) return stale;

    // One tab at a time asks about the profile's device and re-enrols it, so a tab never asks about a row another tab is
    // replacing (it would be answered signed out)
    const outcome = await withDeviceEnrolLock(
      profileId,
      () => this.__askAboutDevice(profileId, current),
      DEVICE_LOCK_WAIT_MS,
    );
    if (!outcome || !current()) return stale;
    if (outcome.signedOut) {
      await this.__forgetWallet(profileId, flight.live);
      return { kind: 'signedOut' };
    }
    const deviceId = outcome.deviceId;
    await this.__watchDevice(profileId, deviceId, { current, start: 'checked' });
    return { kind: 'signedIn', deviceId };
  }

  /**
   * The device check's questions to the server, run under the profile's device lock (__checkDevice): whether this device's
   * stored row is signed in (recording a row for a device that has none), then, when this browser's device key pair is no
   * longer the row's, enrolling a new row in its place. Returns whether the device is signed out, or the row to watch;
   * undefined when the check went stale. A signed-in answer naming another row (one recorded, or the row the server
   * followed the replaced row's replacedBy link to) moves the stored id there. A "signed out" answer about a row another tab has
   * just replaced (its new id now stored) isn't one: that row is watched instead. Nothing is asked once the check is
   * stale, so a check left waiting on the lock neither calls the server nor makes a device key pair after a sign-out.
   */
  private async __askAboutDevice(
    profileId: string,
    current: () => boolean,
  ): Promise<{ signedOut: true } | { signedOut: false; deviceId: string | null } | undefined> {
    const auth = this.__auth;
    if (!auth || !current()) return undefined;
    const storedId = localStorage.getItem(walletStorageKey(profileId, 'deviceId'));
    const replacedHere = () =>
      enrolledByAnotherTab(localStorage.getItem(walletStorageKey(profileId, 'deviceId')), storedId ?? '');
    const body: { deviceId?: string; personId: string; publicKey?: string } = { personId: profileId };
    if (storedId) body.deviceId = storedId;
    let ownKey: string | null = null;
    try {
      ownKey = await devicePublicKeyHex(profileId);
      body.publicKey = ownKey;
    } catch (err) {
      logger.warn('Unable to make a device key for this device', err);
    }
    const output = await withTimeout(
      callLambda('public-ledger/update-device-login-timestamp', 'POST', auth.token, JSON.stringify(body)),
      DEVICE_CALL_TIMEOUT_MS,
    );
    const answer = deviceCheckAnswer(output.res);
    if (answer.signedOut) {
      const replacement = storedId ? replacedHere() : null;
      return replacement ? { signedOut: false, deviceId: replacement } : { signedOut: true };
    }
    // The row recorded, or the one the server followed the replaced row's replacedBy link to: stored even when the check went
    // stale meanwhile, as the server may have moved this device there; the caller watches it only while still current
    const followed = followedDeviceId(storedId, answer.deviceId);
    if (followed) {
      return this.__storeDeviceId(profileId, storedId, followed) ? { signedOut: false, deviceId: followed } : undefined;
    }
    // The profile changed, the wallet was forgotten or replaced, or a newer check started while the call was out
    if (!current()) return undefined;
    if (!storedId) return { signedOut: false, deviceId: null };
    // Asked only once the row is known to be signed in, so a lost key pair never signs a signed-out device back in
    if (!ownKey) return { signedOut: false, deviceId: storedId };
    const enrolled = await this.__enrolledDeviceKey(storedId);
    if (!current()) return undefined;
    if (!deviceKeyReplaced(enrolled, ownKey)) return { signedOut: false, deviceId: storedId };
    const already = replacedHere();
    if (already) return { signedOut: false, deviceId: already };
    const replacement = await this.__enrolNewDevice(profileId, ownKey, storedId);
    if (replacement.signedOut) {
      const other = replacedHere();
      return other ? { signedOut: false, deviceId: other } : { signedOut: true };
    }
    if (!replacement.deviceId) return { signedOut: false, deviceId: storedId };
    // The replaced row is already signed out, so the new id is stored even when the check went stale meanwhile; the
    // caller watches it only while still current
    if (!this.__storeDeviceId(profileId, storedId, replacement.deviceId)) return undefined;
    return { signedOut: false, deviceId: replacement.deviceId };
  }

  // Stores the profile's device id in place of `expected` (the id a check asked about), only while that is still the stored
  // one and the profile's key is still here; returns whether it was stored
  private __storeDeviceId(profileId: string, expected: string | null, deviceId: string): boolean {
    const key = walletStorageKey(profileId, 'deviceId');
    const keyHeld = !!localStorage.getItem(walletStorageKey(profileId, 'privateKey'));
    if (!mayStoreDeviceId(localStorage.getItem(key), expected, keyHeld)) return false;
    localStorage.setItem(key, deviceId);
    return true;
  }

  // The device key enrolled on a plDevice row, or null when it has none or the row can't be read. A read that takes longer
  // than DEVICE_CALL_TIMEOUT_MS throws, failing the device check that holds the lock
  private async __enrolledDeviceKey(deviceId: string): Promise<string | null> {
    try {
      const row = (await withTimeout(
        Promise.resolve(this.__db?.getById('plDevice', deviceId)),
        DEVICE_CALL_TIMEOUT_MS,
      )) as { publicKey?: string | null } | undefined;
      return row?.publicKey ?? null;
    } catch (err) {
      if (err instanceof Errors.RequestTimeout) throw err;
      logger.warn('Unable to read this public ledger device', err);
      return null;
    }
  }

  /**
   * Enrols a new device row for the profile with this browser's device key (public-ledger/update-device-login-timestamp
   * with no deviceId), in place of `replacesDeviceId`: the server signs that row out once the new one is made, and answers
   * signed out (creating nothing) when that row is missing, someone else's or already signed out. Called under the
   * profile's device lock by __askAboutDevice, which stores the new id as soon as it is answered, even when its check went
   * stale meanwhile (the old row is signed out by then), as long as the old id is still the stored one and the key is still
   * here. A call that fails (or times out) leaves the old id stored; the next check asks about it and the server answers
   * with the new row, following the old row's replacedBy link. The watch on the old row stops first, so its sign-out here
   * isn't taken for a remote one.
   */
  private async __enrolNewDevice(
    profileId: string,
    publicKey: string,
    replacesDeviceId: string,
  ): Promise<DeviceCheckAnswer> {
    if (!this.__auth) throw new Error('Missing auth service');
    this.__stopWatch();
    const output = await withTimeout(
      callLambda(
        'public-ledger/update-device-login-timestamp',
        'POST',
        this.__auth.token,
        JSON.stringify({ personId: profileId, publicKey, replacesDeviceId }),
      ),
      DEVICE_CALL_TIMEOUT_MS,
    );
    return deviceCheckAnswer(output.res);
  }

  // Stops the watch on this device's row, and any watch still being started
  private __stopWatch() {
    this.__watchToken += 1;
    this.__deviceWatch.clear();
  }

  /**
   * Watches this device's own plDevice row while a tab is open: a sign-out from another device forgets the key here
   * (once a fresh device check confirms it), and the recovery block opens (DEVICE_SIGNED_OUT_EVENT). Only the latest call
   * watches: it replaces any earlier watch, even one whose read resolves later.
   * @param check for a device check's watch, whether that check may still act, and how it ended: after a check the server
   * answered, a row already signed out when first read is forgotten straight away; after a failed one it is checked again
   * (once per failure, signedOutRowAction)
   */
  private async __watchDevice(
    profileId: string,
    deviceId: string | null,
    check?: { current: () => boolean; start: DeviceWatchStart },
  ) {
    this.__stopWatch();
    const token = this.__watchToken;
    if (!deviceId || !this.__db) return;
    const db = this.__db;
    const signedOut = () => (db.get(`plDevice.${deviceId}`) as { signOut?: boolean } | undefined)?.signOut === true;
    const start = check?.start ?? 'plain';
    const live = () =>
      token === this.__watchToken && this.__walletProfileId === profileId && (check?.current() ?? true);
    try {
      // In the store, so its realtime changes reach the watch
      await db.getById('plDevice', deviceId);
    } catch (err) {
      logger.warn('Unable to read this public ledger device', err);
    }
    if (!live()) return;
    this.__deviceWatch.subscribe(`plDevice.${deviceId}.*`, () => {
      if (token === this.__watchToken && signedOut()) {
        void this.__watchedRowSignedOut(profileId, deviceId, signedOutRowAction(start, false));
      }
    });
    if (signedOut()) await this.__watchedRowSignedOut(profileId, deviceId, signedOutRowAction(start, true));
  }

  /**
   * The watched row was signed out. A tab that enrolled a new row in its place (which signs the old one out) has stored the
   * new id: that row is watched instead. Otherwise a fresh device check confirms the sign-out before the key is
   * forgotten, so a replacement whose new id isn't stored yet isn't taken for a remote sign-out; a watch started right
   * after a check the server answered forgets it straight away (`action`, signedOutRowAction). After a failed check, the
   * one fresh check it asks is marked so its own failure asks no more (`recheckOnce`), and a failed fresh check waits for
   * the tab to be back (`wait`).
   */
  private async __watchedRowSignedOut(profileId: string, deviceId: string, action: SignedOutRowAction) {
    if (this.__walletProfileId !== profileId) return;
    const storedId = localStorage.getItem(walletStorageKey(profileId, 'deviceId'));
    if (watchedDeviceReplaced(storedId, deviceId)) {
      void this.__rewatchStoredDevice(profileId);
      return;
    }
    if (action === 'forget') {
      await this.__forgetWallet(profileId, true);
      return;
    }
    if (action === 'wait') return;
    this.__stopWatch();
    // A fresh check: one already out may have asked before the sign-out
    this.__checkGenerations.next(profileId);
    void this.__recheckDevice(profileId, true, action === 'recheckOnce');
  }

  /**
   * Watches the device id stored for the profile again, once any device check out for it has finished (that check's own
   * watch is then replaced). Used when another tab stored the profile's key or device id.
   */
  private async __rewatchStoredDevice(profileId: string) {
    await this.__deviceFlights.get(profileId)?.promise;
    if (this.__walletProfileId !== profileId) return;
    if (!localStorage.getItem(walletStorageKey(profileId, 'privateKey'))) return;
    try {
      await this.__watchDevice(profileId, localStorage.getItem(walletStorageKey(profileId, 'deviceId')));
    } catch (err) {
      logger.warn('Unable to watch this public ledger device', err);
    }
  }

  /**
   * Another tab changed localStorage. An entry the wallet key decrypts was removed: that tab may have deleted the wallet key,
   * so the cached copy here is dropped. The profile's key was added: its next sign-out is announced here. The profile's key
   * or device id was stored (a recovery, a new account, a check that recorded or enrolled a device): a check still out
   * here stops before acting on its answer, and the stored device is watched. The profile's key was removed (its device was
   * signed out, and that tab forgot it): this tab forgets its copy too, and announces it unless this tab already did.
   */
  private readonly __onStorage = (event: StorageEvent) => {
    if (event.storageArea !== localStorage) return;
    if (walletKeyCacheStale(event)) resetWalletKeyCache();
    const profileId = this.__walletProfileId;
    if (!profileId) return;
    if (storageEventRewatches(event, profileId)) {
      if (event.key === walletStorageKey(profileId, 'privateKey')) this.__signOutNotice.arm(profileId);
      this.__checkGenerations.next(profileId);
      void this.__rewatchStoredDevice(profileId);
      return;
    }
    if (!shouldForgetOnStorageEvent(event, profileId)) return;
    this.__wallet = undefined;
    this.__checkGenerations.next(profileId);
    this.__unconfirmed.delete(profileId);
    this.__stopWatch();
    if (this.__signOutNotice.take(profileId)) {
      this.dispatchEvent(new CustomEvent(DEVICE_SIGNED_OUT_EVENT, { detail: { profileId } }));
    }
  };

  // The realtime connection came back: a sign-out sent while it was down is asked for again
  private readonly __onResync = () => {
    const profileId = this.__walletProfileId;
    if (profileId) void this.__recheckDevice(profileId);
  };

  // The tab is back online or focused: a device check that failed, or a profile held unconfirmed, is checked again
  private readonly __onBack = () => {
    const profileId = this.__walletProfileId;
    if (profileId && (this.__deviceCheckFailed || this.__unconfirmed.has(profileId))) {
      void this.__recheckDevice(profileId);
    }
  };

  // The device check again (or the one already out); with a tab open on the profile (live), a sign-out it finds is
  // announced
  private __recheckDevice(profileId: string, live = true, retry = false): Promise<DeviceCheckOutcome> {
    return this.__startDeviceCheck(profileId, live, retry);
  }

  /**
   * This device was signed out of the profile's account: forgets everything public ledger it holds for the profile (the
   * stored key, device id, any pending key or phrase, the device key pair, the wallet key once nothing needs it) and the
   * copy in memory. A device check still out stops before acting on its answer. A tab open on it is told
   * (DEVICE_SIGNED_OUT_EVENT), once: the watch, a re-check (including one after a refused signature) and another tab can
   * all report the same sign-out, in any order.
   */
  private async __forgetWallet(profileId: string, announce: boolean) {
    this.__stopWatch();
    this.__checkGenerations.next(profileId);
    this.__unconfirmed.delete(profileId);
    const announceNow = this.__signOutNotice.take(profileId) && announce;
    if (this.__walletProfileId === profileId) this.__wallet = undefined;
    await forgetWallet(profileId);
    if (announceNow) this.dispatchEvent(new CustomEvent(DEVICE_SIGNED_OUT_EVENT, { detail: { profileId } }));
  }

  /**
   * A signature (or its challenge) the device gate refused. On DEVICE_SIGNED_OUT a fresh device check runs: a tab that sent
   * the id of a row since replaced (here or in another tab) is answered with the new row (the server follows the replaced
   * row's replacedBy link) and keeps its wallet. Only a check answering signed out forgets the wallet; one answering
   * signed in (on the refused row or another) keeps it. A check that couldn't answer (it failed, timed out or went stale)
   * keeps the wallet but holds the profile unconfirmed in this tab, so signatures and proofs are refused up front until a
   * device check answers (refusedSignatureAction, __confirmDevice). Returns the error to show, or null when the error
   * isn't the gate's.
   */
  private async __deviceRefused(profileId: string, err: unknown, refusedId: string | null): Promise<Error | null> {
    const refusal = deviceRefusal(lambdaErrorCode(err));
    if (!refusal) return null;
    if (refusal.recheck) {
      for (let first = true; ; first = false) {
        // A fresh check: one already out may have asked before the sign-out
        this.__checkGenerations.next(profileId);
        const outcome = await this.__recheckDevice(profileId);
        const storedNow = localStorage.getItem(walletStorageKey(profileId, 'deviceId'));
        const action = refusedSignatureAction(refusedId, outcome, storedNow, first);
        if (action === 'recheck') continue;
        if (action === 'forget') await this.__forgetWallet(profileId, true);
        // The key went while the check was out (forgotten here or in another tab): the device is signed out, not unconfirmed
        if (!localStorage.getItem(walletStorageKey(profileId, 'privateKey'))) {
          this.__unconfirmed.delete(profileId);
          break;
        }
        if (action === 'unconfirm') {
          this.__unconfirmed.add(profileId);
          return new LambdaError(DEVICE_UNCONFIRMED_COPY, lambdaErrorCode(err));
        }
        // Still signed in: the key stays, so the recovery phrase isn't asked for
        if (action === 'keep') return new LambdaError(DEVICE_RECHECKED_COPY, lambdaErrorCode(err));
        break;
      }
    }
    return new LambdaError(refusal.text, lambdaErrorCode(err));
  }

  /**
   * A profile held unconfirmed (__deviceRefused) signs nothing until a device check answers: the check is run again here,
   * at the start of each signature and device proof. Signed in clears the hold; signed out forgets the wallet (the check
   * does) and throws; a check that still can't answer throws, keeping the wallet. Errors are thrown plain, so a caller's
   * __deviceRefused doesn't take them for the gate's.
   */
  private async __confirmDevice(profileId: string) {
    if (!this.__unconfirmed.has(profileId)) return;
    // No key left to confirm: the hold goes, and reading the key gives the real "add your key" refusal
    if (!localStorage.getItem(walletStorageKey(profileId, 'privateKey'))) {
      this.__unconfirmed.delete(profileId);
      return;
    }
    const confirmation = deviceConfirmation(await this.__recheckDevice(profileId));
    if (confirmation === 'signedOut') throw new Error(DEVICE_SIGNED_OUT_COPY);
    if (confirmation === 'unconfirmed') throw new Error(DEVICE_UNCONFIRMED_COPY);
  }

  /**
   * A fresh proof that a signature for `profileId` comes from this device: a challenge from public-ledger/device-challenge
   * for the device id stored for that profile, signed with that profile's device key pair. A device holding a key but no
   * id is checked again first, which records one. Asking replaces the device's last challenge, so it is asked straight
   * before each send. Throws when the open profile is no longer `profileId`, so one profile's proof never goes with
   * another's signature. A refusal is thrown as it came, for the caller's __deviceRefused, and the id the proof was asked
   * for is kept in `proved` (so a refusal is known to be about that row).
   */
  private async __deviceProof(profileId: string, proved: ProvedDevice): Promise<DeviceProof> {
    if (!this.__auth) throw new Error('Missing auth service');
    this.__assertProfile(profileId);
    // A profile held unconfirmed proves nothing until a device check answers
    await this.__confirmDevice(profileId);
    // A device check that failed (offline) is tried again first, so a sign-out or a lost device key it finds is acted on
    if (this.__deviceCheckFailed) await this.__recheckDevice(profileId);
    let deviceId = localStorage.getItem(walletStorageKey(profileId, 'deviceId'));
    if (!deviceId) {
      await this.__recheckDevice(profileId);
      deviceId = localStorage.getItem(walletStorageKey(profileId, 'deviceId'));
    }
    this.__storedPrivateKeyEntry(profileId);
    if (!deviceId) throw new Error(NO_DEVICE_ID_COPY);
    proved.deviceId = deviceId;
    const output = await callLambda(
      'public-ledger/device-challenge',
      'POST',
      this.__auth.token,
      JSON.stringify({ deviceId }),
    );
    const { challengeId, challenge } = output.res as unknown as { challengeId: string; challenge: string };
    const signature = await signDeviceChallenge(profileId, challenge);
    this.__assertProfile(profileId);
    return { deviceId, challengeId, signature };
  }

  // A signature is made, proved and sent for one profile: a profile change part way stops it before anything is sent
  private __assertProfile(profileId: string) {
    if (this.__dir?.me?.id !== profileId) throw new Error(PROFILE_CHANGED_COPY);
  }

  /**
   * Checks every part about to be signed (helpers/public-ledger-sign-check.ts): that the rows say what the card showed,
   * then against the ScheduleCreate the database stored, then against the public ledger's mirror node. On a mismatch it reports it to the
   * PUBLIC_LEDGER team and throws, so nothing is signed. A read that fails, a mirror that can't be read, or a schedule that
   * is no longer open throws too, without a report.
   */
  private async __checkBeforeSigning(
    parts: ServedPart[],
    companyId: string,
    workflowId: string,
    jobId: string,
    shown: ShownOnCard,
  ) {
    const reads = await this.__signCheckReads(parts, companyId);
    const checked: { part: ServedPart; row: PublicLedgerTransaction; context: SignCheckContext }[] = [];
    for (const part of parts) {
      const row = reads.rows.get(part.transactionId);
      if (!row) throw new Error(SIGN_CHECK_UNAVAILABLE_COPY);
      const context: SignCheckContext = {
        companyAccountId: reads.company?.ledgerCred?.accountId,
        message: row.consensusService?.messageId ? reads.messages.get(row.consensusService.messageId) : null,
        ledgerTopicId: row.consensusService?.topicId ? reads.topics.get(row.consensusService.topicId)?.topicId : null,
        publicKeyOf: (personId) => reads.people.find((person) => person.id === personId)?.ledgerCred?.publicKey,
      };
      // What was read now must be what the card showed, or the check would vouch for something the person never saw
      const changed = shownMismatch(row, context.message, shown);
      if (changed) await this.__refuseSigning(part.transactionId, workflowId, jobId, changed);
      const result = checkSigningPart(part, row, reads.operating, context);
      if (!result.ok) await this.__refuseSigning(part.transactionId, workflowId, jobId, result.reason);
      checked.push({ part, row, context });
    }

    // Read from the network the app is built for, never one the database alone names (helpers/public-ledger-mirror.ts)
    const network = mirrorNetwork(this.__app?.mode as string | undefined, reads.company?.ledgerCred?.network);
    if (!network) throw new Error(SIGN_CHECK_UNAVAILABLE_COPY);
    for (const { part, row, context } of checked) {
      const record = await fetchMirrorSchedule(network, row.scheduleId ?? '', {
        fetch: (url, init) => fetch(url, init),
        sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      });
      const result = checkSigningPartOnMirror(record, row, reads.operating, context, Math.floor(Date.now() / 1000));
      if (result.ok) continue;
      if (result.reason === 'MIRROR_UNAVAILABLE') throw new Error(MIRROR_UNAVAILABLE_COPY);
      if (result.reason === 'SCHEDULE_CLOSED') throw new Error(SCHEDULE_CLOSED_COPY);
      await this.__refuseSigning(part.transactionId, workflowId, jobId, result.reason);
    }
  }

  // A part didn't match: logs it, reports it to the PUBLIC_LEDGER team and throws, so nothing is signed
  private async __refuseSigning(
    transactionId: string,
    workflowId: string,
    jobId: string,
    reason: SignCheckReason,
  ): Promise<never> {
    logger.error(`Refused to sign ${transactionId}: ${reason}`);
    const reported = await this.__reportSignMismatch(transactionId, workflowId, jobId, reason);
    throw new Error(reported ? SIGN_MISMATCH_COPY : SIGN_MISMATCH_UNREPORTED_COPY);
  }

  // What the check compares the served parts with: their rows, messages and topics, the company, its operating account and
  // (for a signatory change) the colleagues' public keys
  private async __signCheckReads(parts: ServedPart[], companyId: string) {
    const db = this.__db;
    if (!db) throw new Error('Missing db service');
    try {
      const rows = new Map<string, PublicLedgerTransaction>();
      for (const part of parts) {
        const row = await db.getById<PublicLedgerTransaction & ButtressEntity>('plTransaction', part.transactionId);
        if (row) rows.set(row.id, row);
      }
      const messages = new Map<string, PublicLedgerMessage>();
      const topics = new Map<string, PublicLedgerTopic>();
      for (const row of rows.values()) {
        const { messageId, topicId } = row.consensusService ?? {};
        if (messageId && !messages.has(messageId)) {
          const message = await db.getById<PublicLedgerMessage & ButtressEntity>('plMessage', messageId);
          if (message) messages.set(messageId, message);
        }
        if (topicId && !topics.has(topicId)) {
          const topic = await db.getById<PublicLedgerTopic & ButtressEntity>('plTopic', topicId);
          if (topic) topics.set(topicId, topic);
        }
      }
      const company = await db.getById<Company & ButtressEntity>('company', companyId);
      const operatingRows = await db.query('plOperatingAccount', { companyId: { $eq: companyId } });
      // The server refuses a company with more than one operating account, so the check never picks one of several
      const operatingResults = (operatingRows.results ?? []) as unknown as OperatingAccount[];
      if (operatingResults.length !== 1)
        throw new Error(`Expected one operating account, found ${operatingResults.length}`);
      const [operating] = operatingResults;
      const changesKey = [...rows.values()].some((row) => row.type === 'UPDATE_ACCOUNT_TRANSACTION');
      const people = changesKey
        ? ((await db.query('people', { companyId: { $eq: companyId } })).results as unknown as Person[])
        : [];
      return { rows, messages, topics, company, operating, people };
    } catch (err) {
      logger.error('Unable to read what a signature is checked against', err);
      throw new Error(SIGN_CHECK_UNAVAILABLE_COPY);
    }
  }

  // What an approval is checked against: the company's account and network, and its one operating account (#24's user read
  // of plOperatingAccount, DECISION 2.5)
  private async __approveCheckReads(companyId: string) {
    const db = this.__db;
    if (!db) throw new Error('Missing db service');
    try {
      const company = await db.getById<Company & ButtressEntity>('company', companyId);
      const operatingRows = await db.query('plOperatingAccount', { companyId: { $eq: companyId } });
      const operatingResults = (operatingRows.results ?? []) as unknown as OperatingAccount[];
      if (operatingResults.length !== 1 || !operatingResults[0]?.accountId) {
        throw new Error(`Expected one operating account, found ${operatingResults.length}`);
      }
      return {
        companyAccountId: company?.ledgerCred?.accountId ?? null,
        network: company?.ledgerCred?.network,
        operating: operatingResults[0],
      };
    } catch (err) {
      logger.error('Unable to read what an approval is checked against', err);
      throw new Error(SIGN_CHECK_UNAVAILABLE_COPY);
    }
  }

  // Tells the PUBLIC_LEDGER team the browser refused to sign. Only ids and the reason code are sent, never bytes or keys.
  // Never throws: whether it was told is returned
  private async __reportSignMismatch(
    transactionId: string,
    workflowId: string,
    jobId: string,
    reason: SignCheckReason,
  ) {
    try {
      if (!this.__auth) throw new Error('Missing auth service');
      await callLambda(
        'public-ledger/report-sign-mismatch',
        'POST',
        this.__auth.token,
        JSON.stringify({ transactionId, workflowId, jobId, reason }),
      );
      return true;
    } catch (err) {
      logger.error('Unable to report a signature that did not match', err);
      return false;
    }
  }

  /**
   * The profile's stored private key entry, read from localStorage (never the copy in memory, so a key forgotten in
   * another tab isn't signed with here). Throws when this device holds none.
   */
  private __storedPrivateKeyEntry(profileId: string): string {
    const entry = localStorage.getItem(walletStorageKey(profileId, 'privateKey'));
    if (!entry) throw new Error('Missing wallet credentials. Please add your public ledger creds to this device');
    return entry;
  }

  /**
   * The private key of the profile's wallet on this device, read from storage and decrypted to sign with
   */
  private async __walletPrivateKey(profileId: string): Promise<PrivateKey> {
    const entry = this.__storedPrivateKeyEntry(profileId);
    try {
      return PrivateKey.fromString(await decryptWalletSecret(entry));
    } catch {
      throw new Error(
        "Your public ledger key on this device can't be read. Enter your recovery phrase to add it to this device again.",
      );
    }
  }

  /**
   * The key pair for an account creation: the one kept from an earlier attempt that hasn't finished, or a new one, kept
   * (encrypted) before the server is called so that a retry creates the account with the same key and phrase
   */
  private async __pendingOrNewKeyPair(
    prefix: string,
  ): Promise<{ privateKey: PrivateKey; phrase: string; privateKeyEntry: string }> {
    const pendingKey = localStorage.getItem(walletStorageKey(prefix, 'pendingPrivateKey'));
    const pendingPhrase = localStorage.getItem(walletStorageKey(prefix, 'pendingMnemonic'));
    if (walletEntryState(pendingKey) === 'ENCRYPTED' && walletEntryState(pendingPhrase) === 'ENCRYPTED') {
      try {
        const phrase = await decryptWalletSecret(pendingPhrase as string);
        const privateKey = await privateKeyFromPhrase(phrase);
        return { privateKey, phrase, privateKeyEntry: pendingKey as string };
      } catch {
        // Unreadable (the wallet key was cleared): a new pair is made below. The server refuses it if the person's account
        // was already made with the old one.
        logger.warn('Discarding an unreadable pending public ledger key');
      }
    }

    const mnemonic = await Mnemonic.generate();
    const phrase = mnemonic.toString();
    const privateKey = await mnemonic.toEd25519PrivateKey();
    const privateKeyEntry = await encryptAndStoreWalletSecret(
      walletStorageKey(prefix, 'pendingPrivateKey'),
      privateKey.toStringDer(),
    );
    await encryptAndStoreWalletSecret(walletStorageKey(prefix, 'pendingMnemonic'), phrase);
    localStorage.setItem(walletStorageKey(prefix, 'pendingPublicKey'), privateKey.publicKey.toStringDer());
    return { privateKey, phrase, privateKeyEntry };
  }

  private __clearPendingKeyPair(prefix: string) {
    localStorage.removeItem(walletStorageKey(prefix, 'pendingPrivateKey'));
    localStorage.removeItem(walletStorageKey(prefix, 'pendingMnemonic'));
    localStorage.removeItem(walletStorageKey(prefix, 'pendingPublicKey'));
  }

  /**
   * @method __createAccount: sends this device's id (if it has one) and its device key, so the server reuses this device's
   * row only, or makes a new one
   * @param { string } profileId
   * @param { string } jobId the caller's @ns/create-public-ledger-account job
   * @param { string } publicKey the new account's ED25519 public key, DER-encoded hex
   * @param { LedgerNetwork | null } network
   * @returns
   */
  private async __createAccount(
    profileId: string,
    jobId: string,
    publicKey: string,
    network: LedgerNetwork | null,
  ): Promise<{
    accountId: string;
    deviceId: string | null;
  } | null> {
    if (!this.__auth) throw new Error('Missing auth service');
    if (!this.__dir) throw new Error('Missing directory service');
    if (!this.__dir.me || !this.__dir.me.id) throw new Error('Missing directoryService profile');
    if (!this.__dir.company || !this.__dir.company.id) throw new Error('Missing directoryService company');

    const body: Record<string, string> = {
      publicKey,
      personId: this.__dir.me.id,
      companyId: this.__dir.company.id,
      jobId,
      devicePublicKey: await devicePublicKeyHex(profileId),
    };
    const deviceId = localStorage.getItem(walletStorageKey(profileId, 'deviceId'));
    if (deviceId) body.deviceId = deviceId;
    if (network) body.network = network;

    const output = await callLambda('public-ledger/create-account', 'POST', this.__auth.token, JSON.stringify(body));

    return output.res as unknown as {
      accountId: string;
      deviceId: string | null;
    };
  }

  /**
   * @method __getAccountId: the account check on a new device: the mirror's free copy first, else a lookup nodeStream pays for.
   * Sends this device's id (if it has one) and its device key: a recovery reuses only this device's own row, so it can't
   * sign a lost look-alike device back in
   * @param { string } profileId
   * @param { string } publicKey the public key the recovery phrase gives, DER-encoded hex
   * @returns
   */
  private async __getAccountId(profileId: string, publicKey: string) {
    if (!this.__auth) throw new Error('Missing auth service');
    if (!this.__dir) throw new Error('Missing directoryService');
    if (!this.__dir.me || !this.__dir.me.id) throw new Error('Missing directoryService profile');

    const body: { personId: string; publicKey: string; devicePublicKey: string; deviceId?: string } = {
      personId: this.__dir.me.id,
      publicKey,
      devicePublicKey: await devicePublicKeyHex(profileId),
    };
    const deviceId = localStorage.getItem(walletStorageKey(profileId, 'deviceId'));
    if (deviceId) body.deviceId = deviceId;
    const output = await callLambda('public-ledger/get-account-id', 'POST', this.__auth.token, JSON.stringify(body));
    return output.res as unknown as { accountId: string; deviceId: string | null };
  }

  /**
   * __getScheduledTransactionSerialisation
   * @param {Object} transaction
   */
  private async __getScheduledTransactionSerialisation(transaction: PublicLedgerTransaction) {
    if (!this.__auth) throw new Error('Missing auth service');
    const output = await callLambda(
      'public-ledger/get-scheduled-transaction-serialisation',
      'POST',
      this.__auth.token,
      JSON.stringify({
        transactionId: transaction.id,
        scheduleId: transaction.scheduleId,
      }),
    );

    // parts: every transaction of the signing group still collecting signatures, the asked-for one included, each with the
    // schedule it signs and its message part
    return output.res as unknown as {
      bytes: string;
      signatories: string[];
      expiresAt?: string | null;
      parts?: { transactionId: string; scheduleId?: string | null; partIndex?: number | null; bytes: string }[];
    };
  }

  /**
   * __submitScheduleTransactionSignatures: every signed part in one call
   * @param {Object[]} signed each transaction's id and the bytes this person signed
   * @param {string} signatoryId the profile that signed, the one the device proof was made for
   * @param {string} workflowId
   * @param {string} jobId
   * @param {DeviceProof} device this device's proof for the server's gate
   */
  private async __submitScheduleTransactionSignatures(
    signed: SignaturePart[],
    signatoryId: string,
    workflowId: string,
    jobId: string,
    device: DeviceProof,
  ) {
    if (!this.__auth) throw new Error('Missing auth service');

    const output = await callLambda(
      'public-ledger/submit-schedule-transaction-signature',
      'POST',
      this.__auth.token,
      JSON.stringify(signatureBatchBody(signed, { signatoryId, workflowId, jobId }, device)),
    );
    const res = output.res as unknown as { resMessage: string };
    return res.resMessage;
  }

  /**
   * @method __addCredentialsToWallet
   * @param prefix the profile's id, which prefixes its wallet's storage keys
   * @param accountId
   * @param publicKey DER-encoded hex
   * @param privateKeyEntry the private key, encrypted with this browser's wallet key
   * @param deviceId
   */
  private __addCredentialsToWallet(
    prefix: string,
    accountId: string,
    publicKey: string,
    privateKeyEntry: string,
    deviceId: string | null,
  ) {
    if (this.__wallet && this.__wallet.publicKey && this.__wallet.privateKeyEntry && this.__wallet.accountId) {
      logger.info('Attempting to inflate credentials that have already been inflated');
      return;
    }

    this.__wallet = { accountId, publicKey, privateKeyEntry };
    this.__signOutNotice.arm(prefix);
    // A device check still out asked about the wallet this one replaces: it stops before acting on its answer
    this.__checkGenerations.next(prefix);

    localStorage.setItem(walletStorageKey(prefix, 'accountId'), accountId);
    localStorage.setItem(walletStorageKey(prefix, 'publicKey'), publicKey);
    localStorage.setItem(walletStorageKey(prefix, 'privateKey'), privateKeyEntry);
    if (deviceId) localStorage.setItem(walletStorageKey(prefix, 'deviceId'), deviceId);
  }

  /**
   * @method __restoreWalletFromLocalStorage: the profile's wallet as stored on this device, read again each time so a key
   * forgotten in another tab is gone here too. The private key stays an entry (ciphertext, or a plain key from an older
   * version until __migratePlainWalletEntry encrypts it).
   * @returns void
   */
  private __restoreWalletFromLocalStorage() {
    if (!this.__dir) throw new Error('Unable to find directoryService');
    if (!this.__dir.me) throw new Error('Unable to find directoryService profile');

    const prefix = this.__dir.me.id;
    if (!prefix || prefix !== this.__walletProfileId) return;

    const accountId = localStorage.getItem(walletStorageKey(prefix, 'accountId'));
    const privateKeyEntry = localStorage.getItem(walletStorageKey(prefix, 'privateKey'));
    const publicKey = localStorage.getItem(walletStorageKey(prefix, 'publicKey'));
    if (!accountId && !privateKeyEntry && !publicKey) {
      this.__wallet = undefined;
      return;
    }
    if (privateKeyEntry) this.__signOutNotice.arm(prefix);
    this.__wallet = {
      ...this.__wallet,
      accountId: accountId ?? '',
      privateKeyEntry: privateKeyEntry ?? '',
      publicKey: publicKey ?? '',
    };
  }

  /**
   * Encrypts a private key an older version stored in plain text, and removes the plain value (concern 7). Runs on load.
   */
  private async __migratePlainWalletEntry() {
    const prefix = this.__dir?.me?.id;
    if (!prefix) return;
    const storageKey = walletStorageKey(prefix, 'privateKey');
    const stored = localStorage.getItem(storageKey);
    if (walletEntryState(stored) !== 'PLAIN') return;
    try {
      // Only if nothing replaced it meanwhile
      const entry = await encryptAndStoreWalletSecret(storageKey, stored as string, stored as string);
      if (entry && this.__wallet && this.__wallet.privateKeyEntry === stored) this.__wallet.privateKeyEntry = entry;
    } catch (err) {
      logger.error('Unable to encrypt the public ledger key stored on this device', err);
    }
  }
}
